import { useToggleExpand } from "@/components/header/ExpandButton";
import HeaderIconButton from "@/components/header/HeaderIconButton";
import { Icon } from "@/components/ui/Icon";
import clsx from "clsx";
import type { PropsWithChildren, ReactNode } from "react";
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { useLauncherStore } from "../../../../lib/client/contexts/LauncherStoreContext";

interface OverlayContextType {
  isOpen: boolean;
  open: (content: ReactNode) => void;
  close: () => void;
}

const OverlayContext = createContext<OverlayContextType | undefined>(undefined);

type OverlayProviderProps = PropsWithChildren<{
  messengerId: string;
}>;

export function OverlayProvider({ children, messengerId }: OverlayProviderProps) {
  const toggleExpand = useToggleExpand(messengerId, true);
  const [isOpen, setIsOpen] = useState(false);
  const [content, setContent] = useState<ReactNode | null>(null);
  const [animationState, setAnimationState] = useState<"closed" | "opening" | "open" | "closing">(
    "closed",
  );

  const { isOpen: appOpen } = useLauncherStore(
    useShallow((state) => ({
      isOpen: state.isOpen,
    })),
  );

  const close = useCallback(() => {
    toggleExpand("collapse");
    setAnimationState("closing");
    setTimeout(() => {
      setIsOpen(false);
      setContent(null);
      setAnimationState("closed");
    }, 150);
  }, [toggleExpand]);

  // Close the overlay when the app is closed
  useEffect(() => {
    if (!appOpen) {
      setIsOpen(false);
      setContent(null);
      setAnimationState("closed");
    }
  }, [appOpen]);

  const open = (content: ReactNode) => {
    setContent(content);
    toggleExpand("expand");
    setIsOpen(true);
    setAnimationState("opening");
    setTimeout(() => {
      setAnimationState("open");
    }, 50);
  };

  return (
    <OverlayContext.Provider value={{ isOpen, open, close }}>
      {children}
      {isOpen && (
        <div
          className={clsx(
            "absolute inset-0 z-[1001] h-full rounded-[inherit] bg-white dark:bg-gray-800",
            "pb-4 transition-all duration-150 ease-in-out",
            animationState === "opening" || animationState === "closing"
              ? "scale-95 opacity-0"
              : "scale-100 opacity-100",
          )}
        >
          <div
            className={clsx(
              "header relative flex h-[60px] shrink-0 items-center justify-between",
              "text-[--contrastText]",
              "bg-[--color500] dark:bg-[--color500]",
              "px-2",
              "rounded-t-[inherit]",
            )}
          >
            <HeaderIconButton onClick={() => close()}>
              <Icon
                icon="dazzle-linear-arrow-left"
                className="cursor-pointer"
                size="text-2xl font-medium"
              />
            </HeaderIconButton>
          </div>
          <div className="h-[calc(100%-60px)] overflow-scroll p-5 scrollbar-hide">{content}</div>
        </div>
      )}
    </OverlayContext.Provider>
  );
}

export function useOverlay() {
  const context = useContext(OverlayContext);
  if (context === undefined) {
    throw new Error("useOverlay must be used within an OverlayProvider");
  }
  return context;
}
